import * as React from "react"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
	Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider,
	FormControlLabel, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material"
import SkipNextIcon from '@mui/icons-material/SkipNext'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import {
	EditBrands, EditCategories, EyecatchEditor, ProductsEditor,
	UnmatchedBrandChips, _resolveUnmatched,
	ensureBrandCatalogue,
} from "../Review"
import {
	items, targetIdx, seenKeys, isLoading, lastValidatedTask,
	selectedDate, selectedRetailerId, selectedSectionId, selectedReason,
	retailers, sections, reasons, totalPending, taskContext,
	clearReviewQueueSignals, resetQueueKeepingDate,
} from "./@signals"


// Max items to fetch per call. Matches the back's _QUEUE_BATCH default
// — keeping them aligned avoids surprises if either side ever overrides.
const BATCH = 11

// Legacy subtype enums per parent kind, still used by the carousel-
// style kinds (product-slider / product-list / brand-logos). For
// banner / slider / interstitial the legacy `type` has been replaced
// by the unified `intent` slot below — those kinds don't show this
// dropdown.
const SUBTYPES_BY_KIND = {
	'product-slider':['top_sales', 'offers', 'news', 'recommended', 'brand', 'other'],
	'product-list':  ['top_sales', 'offers', 'news', 'other'],
	'brand-logos':   ['marca'],
}

// Unified intent vocab — applies to banner / slider / interstitial.
// Mirrors resource_match_svc.INTENTS on the back; the back coerces
// any incoming value through coerce_intent() so a mismatch here is
// safe but produces 'other'.
const INTENTS = [
	'promotion', 'news', 'brand', 'editorial',
	'bestseller', 'category', 'other',
]
// ...but `other` is not offered as a manual pick (869e9gwfh). It is
// first of all the machine's sink: coerce_intent() lands there every
// null / unknown / legacy value the AI or the extension sends. And as a
// chip it doubled up with `editorial`, the real catch-all for
// retailer-curated content — measured on retail 2026-09-02, operators
// picked `other` 1077 times against 1022 for `editorial`, i.e. the same
// drawer twice. Collapsing them onto `editorial` is the ask; the cost,
// stated: an operator who truly cannot classify now has Skip, not a chip.
// It still renders while it IS the current value, so an AI `other` stays
// legible and replaceable instead of showing no selection at all.
const INTENT_CHOICES = INTENTS.filter(i => i !== 'other')
function intentChoices(current) {
	return current === 'other' ? [...INTENT_CHOICES, 'other'] : INTENT_CHOICES
}
const INTENT_KINDS = new Set(['banner', 'slider', 'interstitial'])

// Composition vocab — chips the operator clicks directly. Mirrors
// resource_match_svc.COMPOSITIONS_FRAGRANCE / _NON_FRAGRANCE on the
// back. The back stores the pick as-is AND flags the leaf with
// `composition_locked=true` so future brand edits don't auto-overwrite
// the human's decision.
//
// The applicable vocabulary depends on the leaf's categories: only
// fragrance-only leaves expose the line-similarity refinements
// (monoline / multiline / masterbrand). Every other leaf collapses to
// the four-value reduced set, where composition NEVER triggers QA.
// `allowedCompositions(categories)` mirrors the back rule so the chip
// set is reactive to category edits in the same component.
const COMPOSITIONS_FRAGRANCE = [
	{ value: 'multicompany', label: 'Multicompany' },
	{ value: 'monocompany',  label: 'Monocompany'  },
	{ value: 'monobrand',    label: 'Monobrand'    },
	{ value: 'monoproduct',  label: 'Monoproduct'  },
	{ value: 'monoline',     label: 'Monoline'     },
	{ value: 'multiline',    label: 'Multiline'    },
	{ value: 'masterbrand',  label: 'Masterbrand'  },
]
const COMPOSITIONS_NON_FRAGRANCE = COMPOSITIONS_FRAGRANCE
	.filter(o => ['multicompany', 'monocompany', 'monobrand', 'monoproduct'].includes(o.value))

function isFragranceOnly(categories) {
	const cats = (categories || []).filter(Boolean)
	return cats.length === 1 && cats[0] === 'fragrances'
}

function allowedCompositions(categories) {
	return isFragranceOnly(categories) ? COMPOSITIONS_FRAGRANCE : COMPOSITIONS_NON_FRAGRANCE
}


// Best-effort parser for the `qa_reason` strings the AI review and
// the brand-catalog-gap trigger emit:
//   - "unmatched brand(s): Melt Cosmetics"
//   - "unmatched brand(s): A, B, C…"
//   - "brand catalog gap: A, B"
// Returns the comma-separated brand strings so UnmatchedBrandChips
// can render them clickable. Returns [] when the reason doesn't
// carry the known prefixes.
function _parseUnmatchedFromQaReason(reason) {
	if (!reason || typeof reason !== 'string') return []
	const m = reason.match(/(?:unmatched brand\(s\)|brand catalog gap)\s*:\s*(.+?)\s*(?:[…]|\.\.\.|$)/i)
	if (!m) return []
	return m[1].split(',').map(s => s.trim()).filter(Boolean)
}


// Defensive sanitiser for `tagging.brands`. MUI Autocomplete expects
// {id_brand, name} dicts; the matcher's MISS path can leave behind
// raw strings on a resource if the AI normaliser hasn't run yet, and
// those silently break the input focus. Drop non-objects so the
// remaining canonical entries still render.
function _sanitiseBrands(raw) {
	if (!Array.isArray(raw)) return []
	const out = []
	for (const b of raw) {
		if (b && typeof b === 'object' && b.id_brand != null) {
			out.push(b)
		} else if (typeof b === 'string' && b) {
			// eslint-disable-next-line no-console
			console.warn('[ReviewQueue] resource has raw brand string in tagging.brands; dropping:', b)
		}
	}
	return out
}


// Adapt a back's `{id_resource, tagging, preview_occurrence, ...}`
// payload into the flat item shape the existing ItemCard renderer
// expects. Defined at module scope (NOT inside the component) so
// the function identity is stable across renders — recreating it on
// every render was forcing loadMore to capture a fresh closure each
// time, racing the concurrent auto-load-more useEffect.
function _adaptResource(res) {
	const occ = res?.preview_occurrence || {}
	const tagging = res?.tagging || {}
	return {
		id_resource: res?.id_resource,
		// The sighting being shown. Sent back on validate so a per-crop
		// edit (eyecatch / sponsored) lands on THIS occurrence and not on
		// every appearance of the creative — 869ehankw.
		id_occurrence: occ.id_occurrence,
		parent_kind:      occ.parent_kind || 'banner',
		item_kind:        occ.leaf_kind || occ.parent_kind || 'banner',
		// The VT_GCS_BUCKET is not public-read, so a raw crop_url
		// won't render in the browser. The back inlines the preview
		// crop as a data:image/png URI on `preview_occurrence.crop`;
		// fall back to crop_url only as a last resort.
		crop:             occ.crop || occ.crop_url || res?.crop_path || null,
		destination_url:  tagging.destination_url || occ.destination_url || '',
		task_date:    occ.seen_at ? String(occ.seen_at).slice(0, 10) : '',
		// Who captured the sighting (resolved on the back: task owner's email).
		author:       occ.author || '',
		// Retailer comes pre-resolved from the back (added v1.26.x):
		// {id_retailer, slug, name, country}. Fall back to a synthesised
		// label only if the back is older and doesn't ship it.
		retailer:     res?.retailer
			? {
				id_retailer: res.retailer.id_retailer,
				slug:        res.retailer.slug || '',
				name:        res.retailer.name || res.retailer.slug || `retailer #${res.retailer.id_retailer}`,
				country:     res.retailer.country || '',
			}
			: { name: `retailer #${res?.id_retailer ?? '?'}` },
		// Section resolved on the back (task → section). Fall back to the
		// opaque task id only when an older back doesn't ship it.
		section:      occ.section
			? {
				id_section: occ.section.id_section,
				name:       occ.section.name || occ.section.slug || `section #${occ.section.id_section}`,
				slug:       occ.section.slug || '',
				kind:       occ.section.kind || '',
			}
			: (occ.id_task ? { name: `task #${occ.id_task}` } : { name: '' }),
		// Per-leaf kind override. 'banner' means an operator reclassified
		// this product-list member as a mis-captured banner. Drives the
		// editor's effective kind (see ItemCard).
		leaf_kind:      tagging.leaf_kind || '',
		type:           tagging.type || '',
		intent:         tagging.intent || '',
		composition:    tagging.composition || '',
		product_count:  tagging.product_count ?? null,
		line_relation:  tagging.line_relation ?? 'unknown',
		categories:     Array.isArray(tagging.categories) ? tagging.categories : [],
		brands:         _sanitiseBrands(tagging.brands),
		// Per-SIGHTING since 869ehankw: the occurrence's own value wins,
		// and only falls back to the creative's while migration 042 has
		// not removed the keys from it. The queue shows one representative
		// crop, so what the operator edits here is what THAT crop showed.
		eyecatch:       Array.isArray(occ.eyecatch) ? occ.eyecatch
		                : (Array.isArray(tagging.eyecatch) ? tagging.eyecatch : []),
		sponsored:      occ.sponsored != null ? !!occ.sponsored : !!tagging.sponsored,
		product:        tagging.product || '',
		products:       Array.isArray(tagging.products) ? tagging.products : [],
		notes:          tagging.notes || '',
		count_seen:        res?.count_seen,
		first_seen_at:     res?.first_seen_at,
		last_seen_at:      res?.last_seen_at,
		ai_review: {
			needs_qa: !!res?.needs_qa,
			qa_reason: res?.qa_reason || '',
			unmatched_brands: _parseUnmatchedFromQaReason(res?.qa_reason || ''),
		},
	}
}


export default function ReviewQueuePage() {
	const { showToast } = useAppToast()
	// Local copy of the editable fields for the current item. We don't
	// mutate the signal items[] directly — that would dirty the queue
	// state mid-session. On "Validate" we POST `draft` and advance.
	const [draft, setDraft] = useState(null)
	// Lightbox state: when not null, holds the data URI to zoom in on.
	// Kept local — there's only ever one image to zoom (the current
	// item's crop), no sibling navigation needed.
	const [zoomSrc, setZoomSrc] = useState(null)
	// Raw→canonical brand resolutions the operator made on THIS resource via
	// the "AI unmatched" chips. On validate we propagate them to sibling
	// pending resources carrying the same unmatched brand. Reset per item.
	const [resolvedPairs, setResolvedPairs] = useState([])

	const current = items.value[targetIdx.value] || null
	// `?seed=<id_resource>` consumed once at first load. The Resources
	// Dashboard's "Validate →" link uses it to surface a specific
	// resource at the top of the batch.
	// `?id_task=<id>` narrows the queue to the resources flagged in
	// that one task (site / section / date). The Pipeline dashboard's
	// per-row "QA: N" chip deep-links here.
	const location = useLocation()
	const navigate = useNavigate()
	const seedParam = React.useMemo(() => {
		const sp = new URLSearchParams(location.search)
		const raw = sp.get('seed')
		const n = raw && /^\d+$/.test(raw) ? Number(raw) : null
		return n
	}, [location.search])
	const taskParam = React.useMemo(() => {
		const sp = new URLSearchParams(location.search)
		const raw = sp.get('id_task')
		const n = raw && /^\d+$/.test(raw) ? Number(raw) : null
		return n
	}, [location.search])


	// ── Data fetching ──────────────────────────────────────────────
	// Resource-centric queue (v1.15.x backend). The adapter
	// `_adaptResource` lives at MODULE scope (above) so its identity
	// stays stable across renders — having it inside the component
	// forced loadMore to capture a fresh closure each render, which
	// raced the concurrent auto-load-more useEffect on mount and
	// could crash with "Rendered fewer hooks than expected".
	const loadMore = async (opts) => {
		// Guard against concurrent fetches: the mount useEffect and
		// the auto-load-more useEffect both fire on first paint
		// (targetIdx=0, items.length=0). Without this check both
		// would POST in parallel, return overlapping batches, and
		// double-push them into items.value — wasted bandwidth and
		// occasional render-mid-flight glitches.
		if (isLoading.value) return
		isLoading.value = true
		try {
			const r = await api.post(
				`${BACKEND_APIS.VT}/admin/review-queue/load-resources`,
				{
					limit: BATCH,
					// exclude by id_resource (the new key). The old triple
					// is gone — the back only knows resources here.
					exclude: seenKeys.value,
					id_retailer: selectedRetailerId.value || undefined,
					// Cascade filters (Date → Retailer → Section). Any may be
					// left unset; the queue loads with whatever is scoped.
					date: selectedDate.value || undefined,
					id_section: selectedSectionId.value || undefined,
					// IA-reason facet (normalized qa_reason bucket).
					reason: selectedReason.value || undefined,
					// Pass through the seed param ONCE on first load — the
					// back surfaces this resource at the top of the batch.
					seed: opts?.seed || undefined,
					// Pass through the task scope on every load — it stays
					// sticky for the lifetime of the page. The Pipeline
					// dashboard's "QA: N" chip deep-links here.
					id_task: taskParam || undefined,
				},
			)
			if (!r.ok) {
				showToast.error('Failed to load queue: ' + r.error)
				return
			}
			const fresh = (r.data?.items || []).map(_adaptResource)
			items.value = [...items.value, ...fresh]
			seenKeys.value = [
				...seenKeys.value,
				...fresh.map(it => it.id_resource),
			]
			if (typeof r.data?.total_pending === 'number') {
				totalPending.value = r.data.total_pending
			}
			// NOTE: the retailer dropdown is populated by loadRetailers()
			// (scoped to retailers with pending resources on the chosen
			// date), NOT by load-resources' selectable list — so we do
			// NOT overwrite retailers.value here.
			if (r.data?.task_context !== undefined) {
				taskContext.value = r.data.task_context
			}
		} finally {
			isLoading.value = false
		}
	}

	const refresh = async () => {
		const keepDate = selectedDate.value
		const keepRetailer = selectedRetailerId.value
		const keepSection = selectedSectionId.value
		clearReviewQueueSignals()
		selectedDate.value = keepDate
		selectedRetailerId.value = keepRetailer
		selectedSectionId.value = keepSection
		await loadRetailers(keepDate)
		if (keepRetailer) await loadSections(keepRetailer, keepDate)
		await loadReasons(keepDate, keepRetailer, keepSection)
		await loadMore()
	}

	// ── Cascade: Date → Retailer → Section ──────────────────────────
	// Each level's dropdown only lists options with pending resources for
	// the scope chosen so far. The operator can stop at any level and the
	// queue loads with whatever is scoped. `date` empty = any date.
	const loadRetailers = async (date) => {
		const r = await api.get(
			`${BACKEND_APIS.VT}/admin/review-queue/available/retailers?date=${date || ''}`
		)
		if (r.ok) retailers.value = r.data?.items || []
	}
	const loadSections = async (id_retailer, date) => {
		if (!id_retailer) { sections.value = []; return }
		const r = await api.get(
			`${BACKEND_APIS.VT}/admin/review-queue/available/sections?id_retailer=${id_retailer}&date=${date || ''}`
		)
		if (r.ok) sections.value = r.data?.items || []
	}
	// Available IA reasons for the current scope (date + retailer + section).
	const loadReasons = async (date, id_retailer, id_section) => {
		const qs = new URLSearchParams()
		if (date) qs.set('date', date)
		if (id_retailer) qs.set('id_retailer', id_retailer)
		if (id_section) qs.set('id_section', id_section)
		const r = await api.get(
			`${BACKEND_APIS.VT}/admin/review-queue/available/reasons?${qs.toString()}`
		)
		if (r.ok) reasons.value = r.data?.items || []
	}

	const onDateChange = async (value) => {
		// New date → reset the downstream levels + reason, reload their
		// available options, then pull a fresh scoped batch.
		selectedDate.value = value
		selectedRetailerId.value = ''
		selectedSectionId.value = ''
		selectedReason.value = ''
		sections.value = []
		resetQueueKeepingDate()
		await loadRetailers(value)
		await loadReasons(value, '', '')
		await loadMore()
	}
	const onRetailerChange = async (value) => {
		selectedRetailerId.value = value
		selectedSectionId.value = ''
		selectedReason.value = ''
		resetQueueKeepingDate()
		await loadSections(value, selectedDate.value)
		await loadReasons(selectedDate.value, value, '')
		await loadMore()
	}
	const onSectionChange = async (value) => {
		selectedSectionId.value = value
		selectedReason.value = ''
		resetQueueKeepingDate()
		await loadReasons(selectedDate.value, selectedRetailerId.value, value)
		await loadMore()
	}
	const onReasonChange = async (value) => {
		selectedReason.value = value
		resetQueueKeepingDate()
		await loadMore()
	}


	// ── Init ────────────────────────────────────────────────────────
	useEffect(() => {
		// Warm the brand catalogue cache so the autocomplete inside
		// EditBrands has options to show as soon as the operator types.
		// Fire-and-forget — the component handles the empty/loading
		// state on its own via helperText.
		ensureBrandCatalogue().catch(() => {})
		// Populate the retailer + reason dropdowns with what's pending
		// (any date, since none is selected yet).
		loadRetailers(selectedDate.value)
		loadReasons(selectedDate.value, '', '')
		// Pass `seed` through on the very first load only. After
		// that, exclude_keys will already include it, so subsequent
		// pages won't try to surface it again.
		loadMore({ seed: seedParam })
		return clearReviewQueueSignals
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// React to URL-scope changes (operator hits "Clear filter" in the
	// task-context banner, or navigates to another deep-link without
	// remounting the page). The ref guard skips the initial render so
	// this doesn't double-fire alongside the mount effect above.
	const firstTaskParamRender = React.useRef(true)
	useEffect(() => {
		if (firstTaskParamRender.current) {
			firstTaskParamRender.current = false
			return
		}
		resetQueueKeepingDate()
		loadMore()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskParam])

	// Reset the local draft whenever we move onto a new item.
	useEffect(() => {
		if (!current) { setDraft(null); return }
		setResolvedPairs([])
		setDraft({
			leaf_kind:   current.leaf_kind ?? '',
			type:        current.type ?? '',
			intent:      current.intent ?? '',
			composition: current.composition ?? '',   // read-only, derived in back
			product_count: current.product_count ?? null,
			line_relation: current.line_relation ?? 'unknown',
			categories:  [...(current.categories || [])],
			brands:      [...(current.brands     || [])],
			eyecatch:    [...(current.eyecatch   || [])],
			sponsored:   !!current.sponsored,
			product:     current.product || '',
			products:    JSON.parse(JSON.stringify(current.products || [])),
			notes:       current.notes || '',
			destination_url: current.destination_url || '',
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current?.id_resource])

	// When we run out of items, fetch more transparently — keeps the
	// queue "infinite" from the operator's perspective.
	useEffect(() => {
		if (!isLoading.value && targetIdx.value >= items.value.length) {
			loadMore()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [targetIdx.value, items.value.length])

	// Esc closes the lightbox when open.
	useEffect(() => {
		if (!zoomSrc) return
		const onKey = (e) => { if (e.key === 'Escape') setZoomSrc(null) }
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [zoomSrc])

	// Composition vocabulary depends on draft.categories — if the
	// operator edits categories such that the current composition pick
	// no longer belongs to the allowed set (e.g. was `monoline` while
	// fragrance-only and now categories include `makeup`), clear it.
	// The back's recompute path will re-derive a valid value on
	// validate; here we just keep the UI honest so the operator sees
	// the chip drop out of the row at the moment they change categories.
	useEffect(() => {
		if (!draft || !draft.composition) return
		const allowed = allowedCompositions(draft.categories).map(o => o.value)
		if (!allowed.includes(draft.composition)) {
			setDraft({ ...draft, composition: '' })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(draft?.categories || [])])


	// ── Actions ─────────────────────────────────────────────────────
	const advance = () => {
		targetIdx.value = targetIdx.value + 1
	}

	// Skip releases the lease before advancing so the resource goes
	// back into the pool for someone else. Fire-and-forget — if the
	// release fails the lease auto-expires after 5 min anyway, so we
	// don't block the operator on a network round-trip.
	const skip = () => {
		const id = current?.id_resource
		if (id) {
			api.post(`${BACKEND_APIS.VT}/admin/review-queue/release-resource`, { id_resource: id })
				.catch(() => {})
		}
		advance()
	}

	const validate = async () => {
		if (!current || !draft) return
		isLoading.value = true
		try {
			// Same per-product unmatched_brands scrub as the per-leaf
			// flow: once the operator has assigned canonical brands to
			// a product, the AI's `unmatched_brands` is stale and would
			// otherwise re-raise `needs_qa` on the next read.
			const products = (draft.products || []).map(p => {
				if (!p || !Array.isArray(p.brands) || p.brands.length === 0) return p
				const ai = p.ai_review || {}
				return {
					...p,
					ai_review: {
						...ai,
						unmatched_brands: [],
						needs_qa: false,
						qa_reason: '',
					},
				}
			})
			const patch = {
				// One-way reclassification: only sent when set to 'banner'.
				// The back guards it (product-list member → banner only).
				...(draft.leaf_kind === 'banner' ? { leaf_kind: 'banner' } : {}),
				type:        draft.type,
				intent:      draft.intent,
				composition: draft.composition,
				categories:  draft.categories,
				brands:      draft.brands,
				eyecatch:    draft.eyecatch,
				sponsored:   draft.sponsored,
				product:     draft.product,
				products,
				notes:       draft.notes,
				destination_url: draft.destination_url,
			}
			const r = await api.post(
				`${BACKEND_APIS.VT}/admin/review-queue/validate-resource`,
				{ id_resource: current.id_resource, patch,
				  id_occurrence: current.id_occurrence },
			)
			if (!r.ok) {
				showToast.error('Validate failed: ' + r.error)
				return
			}
			// There is no propagation to wait for any more: the back
			// validates the resource and stamps every one of its
			// sightings in ONE transaction, so by the time this
			// response exists the work is done. The old `in_progress` /
			// `partial` states came from the fan-out (one Cloud Task per
			// occurrence), removed in Phase 3 of the storage redesign —
			// with it went "validated, propagating…" and the support
			// ticket it used to generate.
			const nTasks = r.data?.tasks_refreshed
			showToast.success(
				nTasks
					? `Validated. ${nTasks} task${nTasks === 1 ? '' : 's'} refreshed.`
					: 'Validated.'
			)
			// Propagate any unmatched-brand resolutions to sibling pending
			// resources carrying the same brand (global). Fire after the
			// primary validate so this resource is already out of the pool.
			if (resolvedPairs.length > 0) {
				const br = await api.post(
					`${BACKEND_APIS.VT}/admin/review-queue/bulk-resolve-brand`,
					{ pairs: resolvedPairs },
				)
				if (br.ok && (br.data?.updated || 0) > 0) {
					const { updated, auto_validated, still_pending } = br.data
					showToast.success(
						`Applied to ${updated} other pending resource${updated === 1 ? '' : 's'} `
						+ `(${auto_validated} auto-validated, ${still_pending} still pending).`
					)
					// Those we auto-validated are no longer pending — refresh
					// the counter/dropdowns so the numbers stay honest.
					totalPending.value = Math.max(0, totalPending.value - (auto_validated || 0))
				}
			}
			advance()
		} finally {
			isLoading.value = false
		}
	}


	// ── Render ─────────────────────────────────────────────────────
	const remaining = items.value.length - targetIdx.value
	const empty = !current && !isLoading.value

	const clearTaskScope = () => {
		// Strip `id_task` from the URL; the taskParam-watching effect
		// above sees the change, resets the queue, and reloads the
		// global pool. No need to call loadMore directly here.
		const sp = new URLSearchParams(location.search)
		sp.delete('id_task')
		navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true })
		taskContext.value = null
	}

	return <PageWrapper {...vtPageProps('Review Queue', 'QA each creative once — verdict propagates to every occurrence')}>
		<Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
			{taskContext.value && (
				<Alert
					severity="info"
					sx={{ mb: 2 }}
					action={
						<Button color="inherit" size="small" onClick={clearTaskScope}>
							Clear filter
						</Button>
					}
				>
					Filtered by task <strong>#{taskContext.value.id_task}</strong>
					&nbsp;·&nbsp;{taskContext.value.retailer?.name || taskContext.value.retailer?.slug}
					&nbsp;/&nbsp;{taskContext.value.section?.name || taskContext.value.section?.slug}
					&nbsp;·&nbsp;{taskContext.value.task_date}
					{taskContext.value.asset_path ? ` · ${taskContext.value.asset_path}` : ''}
				</Alert>
			)}
			{/* Title + status row */}
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
				<Typography variant="h5" sx={{ fontWeight: 700 }}>
					Review Queue
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Chip
					label={(() => {
						const rname = retailers.value.find(r => r.id_retailer === selectedRetailerId.value)?.name
						return `${totalPending.value} resources pending · ${rname || 'all retailers'}`
					})()}
					color={totalPending.value > 0 ? 'warning' : 'success'}
					size="small"
					sx={{ fontWeight: 700 }}
				/>
				<Chip
					label={`${remaining} in queue`}
					color={remaining > 0 ? 'primary' : 'default'}
					size="small"
				/>
				<Button
					size="small"
					startIcon={<RefreshIcon />}
					onClick={refresh}
					disabled={isLoading.value}
				>Refresh</Button>
			</Stack>

			{/* Cascade filters: Date → Retailer → Section, plus the IA-reason
			    facet. Each level lists only options with pending resources
			    for the scope chosen so far. Stop at any level — the queue
			    loads with whatever is scoped ("" date = any date). Aligned
			    to the top so the per-field helper texts don't misalign the
			    inputs. */}
			<Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
				<TextField
					size="small"
					type="date"
					label="Date"
					value={selectedDate.value}
					onChange={e => onDateChange(e.target.value)}
					disabled={isLoading.value}
					InputLabelProps={{ shrink: true }}
					helperText=" "
					sx={{ width: 170 }}
				/>
				<TextField
					size="small"
					select
					label="Retailer"
					value={selectedRetailerId.value}
					onChange={e => onRetailerChange(e.target.value)}
					disabled={isLoading.value}
					sx={{ width: 220 }}
					helperText={retailers.value.length === 0 ? 'No retailers with pending resources' : ' '}
				>
					<MenuItem value="">All retailers</MenuItem>
					{retailers.value.map(r => (
						<MenuItem key={r.id_retailer} value={r.id_retailer}>
							{r.name}{typeof r.count === 'number' ? ` (${r.count})` : ''}
						</MenuItem>
					))}
				</TextField>
				{/* Disabled inputs swallow pointer events, so the Tooltip is
				    wrapped around a <span> to still surface the hint on hover
				    when no retailer is chosen yet. */}
				<Tooltip title={!selectedRetailerId.value ? 'Pick a retailer first' : ''}>
					<span>
						<TextField
							size="small"
							select
							label="Section"
							value={selectedSectionId.value}
							onChange={e => onSectionChange(e.target.value)}
							disabled={isLoading.value || !selectedRetailerId.value}
							sx={{ width: 220 }}
							helperText={
								!selectedRetailerId.value
									? ' '
									: (sections.value.length === 0 ? 'No sections with pending resources' : ' ')
							}
						>
							<MenuItem value="">All sections</MenuItem>
							{sections.value.map(s => (
								<MenuItem key={s.id_section} value={s.id_section}>
									{s.name}{typeof s.count === 'number' ? ` (${s.count})` : ''}
								</MenuItem>
							))}
						</TextField>
					</span>
				</Tooltip>
				<TextField
					size="small"
					select
					label="IA reason"
					value={selectedReason.value}
					onChange={e => onReasonChange(e.target.value)}
					disabled={isLoading.value}
					sx={{ width: 260 }}
					helperText={reasons.value.length === 0 ? 'No pending reasons in scope' : ' '}
				>
					<MenuItem value="">All reasons</MenuItem>
					{reasons.value.map(r => (
						<MenuItem key={r.reason} value={r.reason}>{r.reason} ({r.count})</MenuItem>
					))}
				</TextField>
			</Stack>

			{isLoading.value && !current && (
				<Stack alignItems="center" sx={{ py: 8 }}>
					<CircularProgress />
				</Stack>
			)}

			{empty && (
				<Alert severity="success" sx={{ mt: 2 }}>
					{selectedDate.value
						? `Nothing pending QA for ${selectedDate.value}. Pick another date or switch to "All dates".`
						: 'Queue is empty — nothing pending QA right now. Check back when more tasks finish the AI review stage.'}
				</Alert>
			)}

			{current && draft && (
				<ItemCard
					item={current}
					draft={draft}
					setDraft={setDraft}
					onSkip={skip}
					onValidate={validate}
					busy={isLoading.value}
					onZoom={src => setZoomSrc(src)}
					onBrandResolved={(raw, canonical) => setResolvedPairs(prev => (
						prev.some(p => p.raw === raw && p.id_brand === canonical.id_brand)
							? prev
							: [...prev, { raw, id_brand: canonical.id_brand, name: canonical.name }]
					))}
				/>
			)}
		</Box>

		{zoomSrc && (
			<Box
				onClick={() => setZoomSrc(null)}
				sx={{
					position: 'fixed', inset: 0, zIndex: 1500,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					bgcolor: 'rgba(12, 26, 62, 0.85)',
					cursor: 'zoom-out',
					p: 4,
				}}
			>
				<Box component="img" src={zoomSrc} alt=""
					sx={{
						maxWidth: '95vw', maxHeight: '95vh',
						objectFit: 'contain',
						boxShadow: '0 12px 60px rgba(0,0,0,0.5)',
						borderRadius: 1,
						background: '#fff',
					}}
				/>
				<Typography
					sx={{
						position: 'absolute', bottom: 16, left: '50%',
						transform: 'translateX(-50%)',
						color: 'rgba(255,255,255,0.75)',
						fontSize: 11,
						textTransform: 'uppercase',
						letterSpacing: 0.05,
						pointerEvents: 'none',
					}}
				>click or ESC to close</Typography>
			</Box>
		)}
	</PageWrapper>
}


function ItemCard({ item, draft, setDraft, onSkip, onValidate, busy, onZoom, onBrandResolved }) {
	// Defensive: if either prop is null/undefined, render an empty
	// placeholder rather than crashing the page. The parent guards
	// with `current && <ItemCard ... />`, but a stale render between
	// signal updates can still pass {} — better to render nothing
	// than to trip a hook-mismatch error in React's recovery path.
	if (!item || !draft) return null
	// Effective kind: when the operator reclassifies a product-list member
	// as a mis-captured banner (draft.leaf_kind === 'banner'), the editors
	// switch to banner even though the container stays 'product-list'.
	const converted = draft.leaf_kind === 'banner'
	const effectiveKind = converted ? 'banner' : item.parent_kind
	// The "Convert to Banner" action is offered ONLY for product-list
	// members that haven't been converted yet (one-way, no revert).
	const canConvertToBanner = item.parent_kind === 'product-list' && !converted
	const subtypes = SUBTYPES_BY_KIND[effectiveKind] || []
	const isIntentKind = INTENT_KINDS.has(effectiveKind)
	// 869ehb2zd — the queue asks the LEAF, not the container. The queue
	// serves a product-slider by rendering the slide with its sub-products,
	// and that is exactly the surface the PRD flagged as most visible: a
	// post-cutover product must get the single-product card, not an editor
	// for a `products[]` it does not have. `item_kind` is the leaf role the
	// back already ships in the queue payload (`_leaf_payload`), and since
	// 869ehb2zd it honours the leaf's own `leaf_kind`.
	const leafKind = converted ? 'banner' : (item.item_kind || effectiveKind)
	const isMultiProduct = leafKind === 'product-slide' && (
		Array.isArray(item.products) && item.products.length > 0
	)
	const isSingleProductCard = leafKind === 'product'
	// Show the legacy Subtype chip ONLY where it makes sense per-leaf.
	//   - product-list fragments: the operator can mark per-fragment
	//     intent (top_sales / offers / news / other) when the list mixes.
	// The legacy Subtype picker no longer applies to any kind in the queue:
	//   - banner / slider / interstitial → use the Intent chips (isIntentKind).
	//   - product-slider → subtype lives on the parent, not per-slide.
	//   - brand-logos → single value, dead weight.
	//   - product-list → subtype doesn't apply (it's a product, not a
	//     promo-typed creative). The brand/category/product fields are the
	//     relevant tagging.
	const showSubtypePicker = false

	return <Paper sx={{ p: 0, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
		<Stack direction={{ xs: 'column', md: 'row' }}>

			{/* ── Crop + context ─────────────────────────────────── */}
			<Box sx={{
				flex: '0 0 500px',
				bgcolor: '#f8f9fb',
				p: 2,
				borderRight: { md: '1px solid' },
				borderBottom: { xs: '1px solid', md: 0 },
				borderColor: { md: 'divider', xs: 'divider' },
			}}>
				<Stack spacing={1}>
					<Typography variant="caption" sx={{ color: 'text.secondary' }}>
						<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
							{item.retailer?.name}
						</Box>
						{item.retailer?.country ? ` · ${item.retailer.country}` : ''}
						{item.section?.name ? ` · ${item.section.name}` : ''}
					</Typography>
					<Stack direction="row" spacing={0.75} flexWrap="wrap">
						<Chip size="small" label={item.task_date} variant="outlined" />
						{item.section?.name && (
							<Chip
								size="small"
								color="secondary"
								variant="outlined"
								label={`Section: ${item.section.name}`}
							/>
						)}
						{(item.count_seen ?? 0) > 0 && (
							<Chip size="small" label={`Seen ${item.count_seen}×`} variant="outlined" />
						)}
						{item.author && (
							<Tooltip title="Captured by">
								<Chip size="small" label={item.author.split('@')[0]} variant="outlined" />
							</Tooltip>
						)}
						{item.id_resource && (
							<Tooltip title="Open resource detail (all data + every occurrence)">
								<Chip
									size="small"
									clickable
									component="a"
									target="_blank"
									href={`/visibility-tracker/resource?id=${item.id_resource}`}
									label={`resource #${item.id_resource}`}
									variant="outlined"
									color="info"
								/>
							</Tooltip>
						)}
						<Chip size="small" label={item.parent_kind} color="primary" variant="outlined" />
						{item.item_kind !== item.parent_kind && (
							<Chip size="small" label={item.item_kind} variant="outlined" />
						)}
					</Stack>
					{item.ai_review?.qa_reason && (
						<Alert severity="warning" sx={{ py: 0.5, px: 1.5 }}>
							<Typography variant="caption" sx={{ fontWeight: 600 }}>
								AI flagged: {item.ai_review.qa_reason}
							</Typography>
						</Alert>
					)}
					{/* Unmatched brands surfaced as interactive chips — clicking
					    one opens a popover with a canonical-brand picker, the
					    same way Banners Review does it. On confirm we persist
					    the mapping (DIPI_BRAND_MAPPING via /admin/brand-mapping)
					    AND patch the local draft so the canonical lands in
					    draft.brands ready to be validated, with the chip
					    removed from the unmatched list. */}
					{item.ai_review?.unmatched_brands?.length > 0 && (
						<UnmatchedBrandChips
							aiReview={item.ai_review}
							onResolve={(raw, canonical) => {
								// Mutate item.ai_review so the chip disappears
								// on the next render (UnmatchedBrandChips reads
								// from item.ai_review directly).
								_resolveUnmatched(item, raw, canonical)
								// Pump the canonical brand into the editable
								// draft so Validate persists it.
								const next = [...(draft.brands || [])]
								if (!next.some(b => b?.id_brand === canonical.id_brand)) {
									next.push({ id_brand: canonical.id_brand, name: canonical.name })
								}
								setDraft({ ...draft, brands: next })
								// Record the raw→canonical pair so Validate can
								// propagate it to sibling pending resources.
								onBrandResolved && onBrandResolved(raw, canonical)
							}}
						/>
					)}
					<Box sx={{
						mt: 1,
						border: '1px solid', borderColor: 'divider',
						borderRadius: 1, overflow: 'hidden',
						bgcolor: '#fff',
						position: 'relative',
					}}>
						{item.crop ? (
							<>
								<img src={item.crop} alt=""
									onClick={() => onZoom && onZoom(item.crop)}
									style={{ width: '100%', display: 'block', cursor: 'zoom-in' }}
								/>
								<Typography
									sx={{
										position: 'absolute', bottom: 4, right: 6,
										color: 'rgba(255,255,255,0.85)',
										fontSize: 10,
										textTransform: 'uppercase',
										letterSpacing: 0.05,
										background: 'rgba(0,0,0,0.45)',
										padding: '2px 6px',
										borderRadius: 3,
										pointerEvents: 'none',
									}}
								>click to zoom</Typography>
							</>
						) : (
							<Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>No crop</Box>
						)}
					</Box>
					{item.destination_url && (
						<Typography variant="caption" sx={{
							color: 'text.secondary',
							wordBreak: 'break-all',
							fontSize: 11,
						}}>
							URL: {item.destination_url}
						</Typography>
					)}
				</Stack>
			</Box>

			{/* ── Editable form ──────────────────────────────────── */}
			<Box sx={{ flex: 1, p: 2.5 }}>
				<Stack spacing={2}>
					{/* Reclassify a banner mis-captured inside a product-list.
					    One-way: once converted the control disappears and the
					    editors below switch to banner (Intent / Composition). */}
					{canConvertToBanner && (
						<Alert
							severity="info"
							sx={{ py: 0.5 }}
							action={
								<Button
									size="small"
									color="inherit"
									variant="outlined"
									onClick={() => setDraft({ ...draft, leaf_kind: 'banner' })}
								>
									Convert to Banner
								</Button>
							}
						>
							<Typography variant="caption">
								This is a <strong>product-list</strong> item. If it's actually a banner
								that slipped into the list, reclassify it.
							</Typography>
						</Alert>
					)}
					{converted && (
						<Alert severity="success" sx={{ py: 0.5 }}>
							<Typography variant="caption">
								Reclassified as <strong>Banner</strong> (kept inside its product-list container).
							</Typography>
						</Alert>
					)}

					{/* Intent — only for banner / slider / interstitial. The
					    legacy `type` chip below is hidden for those kinds
					    (they use intent exclusively). */}
					{isIntentKind && (
						<Box>
							<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.06, color: 'text.secondary', fontWeight: 700 }}>
								Intent
							</Typography>
							<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
								{intentChoices(draft.intent).map(s => (
									<Chip key={s} label={s} size="small"
										color={draft.intent === s ? 'primary' : 'default'}
										variant={draft.intent === s ? 'filled' : 'outlined'}
										onClick={() => setDraft({ ...draft, intent: s })}
										sx={{ cursor: 'pointer' }}
									/>
								))}
							</Stack>
						</Box>
					)}

					{/* Legacy subtype — only useful at per-leaf level for
					    product-list fragments. product-slider's subtype is a
					    parent-banner concept; brand-logos has only one value
					    in the catalogue. See showSubtypePicker above. */}
					{showSubtypePicker && (
						<Box>
							<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.06, color: 'text.secondary', fontWeight: 700 }}>
								Subtype
							</Typography>
							<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
								{subtypes.map(s => (
									<Chip key={s} label={s} size="small"
										color={draft.type === s ? 'primary' : 'default'}
										variant={draft.type === s ? 'filled' : 'outlined'}
										onClick={() => setDraft({ ...draft, type: s })}
										sx={{ cursor: 'pointer' }}
									/>
								))}
							</Stack>
						</Box>
					)}

					{/* Composition — only for banner / slider / interstitial.
					    Direct chip pick; the back stores the choice as-is
					    and marks the leaf as `composition_locked` so a
					    future brand edit doesn't auto-overwrite. The AI's
					    raw inputs (product_count, line_relation) live in
					    tagging_json for audit but are no longer edited
					    here — the operator picks the result directly. */}
					{isIntentKind && (() => {
						const compositionOptions = allowedCompositions(draft.categories)
						const isFragOnly = isFragranceOnly(draft.categories)
						return <Box>
							<Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.06, color: 'text.secondary', fontWeight: 700 }}>
								Composition
							</Typography>
							<Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
								{compositionOptions.map(o => (
									<Chip key={o.value} label={o.label} size="small"
										color={draft.composition === o.value ? 'primary' : 'default'}
										variant={draft.composition === o.value ? 'filled' : 'outlined'}
										// Toggle: clicking the selected chip clears it back to
										// empty. Composition is allowed to be left unset.
										onClick={() => setDraft({
											...draft,
											composition: draft.composition === o.value ? '' : o.value,
										})}
										sx={{ cursor: 'pointer' }}
									/>
								))}
								{draft.composition && (
									<Chip label="Clear" size="small" variant="outlined"
										onClick={() => setDraft({ ...draft, composition: '' })}
										sx={{ cursor: 'pointer', color: 'text.secondary' }}
									/>
								)}
							</Stack>
							{!isFragOnly && (
								<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
									Line-similarity values (monoline / multiline / masterbrand) apply only when categories is exactly <code>fragrances</code>.
								</Typography>
							)}
							{(draft.product_count != null || draft.line_relation) && (
								<Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
									AI saw {draft.product_count ?? '?'} distinct product(s)
									{draft.line_relation && draft.line_relation !== 'unknown' && isFragOnly
										? `, ${draft.line_relation.replace('_', ' ')}`
										: ''}
									.
								</Typography>
							)}
						</Box>
					})()}

					{/* Multi-product (product-slider slide) → ProductsEditor.
					    Otherwise simple per-item brands + categories. */}
					{isMultiProduct ? (
						<ProductsEditor
							products={draft.products}
							onChange={v => setDraft({ ...draft, products: v })}
						/>
					) : (
						<>
							<EditBrands
								value={draft.brands}
								onChange={v => setDraft({ ...draft, brands: v })}
							/>
							<EditCategories
								value={draft.categories}
								onChange={v => setDraft({ ...draft, categories: v })}
							/>
						</>
					)}

					{isSingleProductCard && (
						<>
							<TextField
								size="small" label="Product name"
								value={draft.product || ''}
								onChange={e => setDraft({ ...draft, product: e.target.value })}
							/>
							<EyecatchEditor
								value={draft.eyecatch}
								onChange={v => setDraft({ ...draft, eyecatch: v })}
							/>
							<FormControlLabel
								control={
									<Checkbox size="small"
										checked={!!draft.sponsored}
										onChange={e => setDraft({ ...draft, sponsored: e.target.checked })}
									/>
								}
								label="Sponsored"
							/>
						</>
					)}

					<Divider sx={{ my: 1 }} />

					<Stack direction="row" spacing={1.5} justifyContent="flex-end">
						<Button
							variant="outlined"
							startIcon={<SkipNextIcon />}
							onClick={onSkip}
							disabled={busy}
						>Skip</Button>
						<Button
							variant="contained"
							color="success"
							startIcon={<CheckIcon />}
							onClick={onValidate}
							disabled={busy}
						>Validate</Button>
					</Stack>
				</Stack>
			</Box>
		</Stack>
	</Paper>
}
